import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { airlineImageFn, convertMinutesFn, formatDayWiseDateFn, formatPrice, formatePriceFn } from '../../../helper/commonFunction';
import { ArrowRightIcon , FlightSeatIcon , ExitIconLeft , ExitIconRight, ChevronUpIcon, ChevronDownIcon } from '../../../helper/HwIcon';
import FlightHead from '../../../assets/img/FlightHead.png'
import FlightTail from '../../../assets/img/FlightTail.png'
const SEAT_FEATURE_LABELS = {
  W: 'Window',
  A: 'Aisle',
  N: 'Middle',
  C: 'Centre',
  E: 'Exit row',
  EL: 'Extra legroom',
  K: 'Bulkhead',
  PS: 'Preferred',
  G: 'Comfort',
  B: 'Bassinet',
  H: 'Accessible',
  WG: 'Over wing',
  UP: 'Upper deck',
  Q: 'Quiet zone',
  BD: 'Lie-flat',
};

const buildSeatFeatureLabel = (seat = {}, row = {}) => {
  const labels = [];
  const attributes = Array.isArray(seat?.attributes) ? seat.attributes : [];

  attributes.forEach((code) => {
    const label = SEAT_FEATURE_LABELS[code];
    if (label && !labels.includes(label)) labels.push(label);
  });

  if (!labels.includes('Extra legroom') && seat?.isLegroom) labels.push('Extra legroom');
  if (!labels.includes('Exit row') && row?.isExit) labels.push('Exit row');

  return labels.slice(0, 2).join(', ');
};

function SeatMap({
  seatMap,
  seatSegment,
  allPassengers = [],
  selectedSeats = [],
  onSeatSelect,
  onConfirm,
  activeCurrency,
  displayPrice
}) {
  const [currentPassengerIndex, setCurrentPassengerIndex] = useState(() => {
    // Find first non-infant passenger
    const firstNonInfantIndex = allPassengers.findIndex(p => p.passengerType !== 'INFANT');
    return firstNonInfantIndex >= 0 ? firstNonInfantIndex : 0;
  });

  if (!seatMap || !seatMap.rows) return null;

  // Check if seat is taken by any passenger
  const isSeatTaken = (seatNumber) => {
    return selectedSeats.some(seat => seat.seatNumber === seatNumber);
  };

  // Get seat number for a specific passenger
  const getPassengerSeatNumber = (passengerIndex) => {
    const passengerSeat = selectedSeats.find(seat => seat.passengerIndex === passengerIndex);
    return passengerSeat ? passengerSeat.seatNumber : null;
  };

  // Check if all non-infant passengers have seats
  const allPassengersHaveSeats = () => {
    if (allPassengers.length === 0) return false;
    return allPassengers.every((passenger, idx) => {
      if (passenger.passengerType === 'INFANT') return true; // Skip infants
      return getPassengerSeatNumber(idx) !== null;
    });
  };

  // Calculate total price for all selected seats
  const calculateTotalPrice = () => {
    let total = 0;
    let currency = null;

    selectedSeats.forEach(selectedSeat => {
      // Find the seat data to get price
      seatMap.rows.forEach(row => {
        row.seats.forEach(seat => {
          if (seat.isGap) return;
          const seatNumber = `${row.number}${seat.SeatNumber}`;
          if (selectedSeat.seatNumber === seatNumber) {
            total += parseFloat(seat.SeatCharacteristics || 0);
            if (!currency) currency = seat.CurrencyCode;
          }
        });
      });
    });

    return { total, currency };
  };

  const handleSeatClick = (seatData) => {
    if (onSeatSelect) {
      onSeatSelect(currentPassengerIndex, seatData);

      // Auto-advance to next non-infant passenger
      const nextPassengerIndex = currentPassengerIndex + 1;
      if (nextPassengerIndex < allPassengers.length && allPassengers[nextPassengerIndex].passengerType !== 'INFANT') {
        setCurrentPassengerIndex(nextPassengerIndex);
      }
    }
  };

  const handlePassengerClick = (passengerIndex) => {
    if (allPassengers[passengerIndex].passengerType !== 'INFANT') {
      setCurrentPassengerIndex(passengerIndex);
    }
  };

  const { total: totalPrice, currency } = calculateTotalPrice();

  return (
    <div className="hw-seatmap--aircraft">
      <div className="hw-seatmap--plane-fuselage">
        <img src={FlightHead} style={{width:'100%'}} alt="Plane Head" className="plane-head" />
        <div className="hw-seatmap--fuselage-seats" style={{
          '--plane-head': `url(${FlightHead})`
        }}>
          {seatMap.rows.map((row, rowIdx) => (
            <React.Fragment key={rowIdx}>
              {row.isExit && (
                <div className="hw-seatmap--exit-row">
                  <span className="hw-seatmap--exit-vertical-line"><ExitIconLeft/></span>
                  <span className="hw-seatmap--exit-label">EXIT</span>
                  <span className="hw-seatmap--exit-space"></span>
                  <span className="hw-seatmap--exit-label">EXIT</span>
                  <span className="hw-seatmap--exit-vertical-line"><ExitIconRight/></span>
                </div>
              )}
              <div className="hw-seatmap--row">
                {row.seats.map((seat, seatIdx) => {
                  if (seat.isGap) {
                    return <div className="hw-seatmap--gap" key={`gap-${seatIdx}`}></div>;
                  }
                  const seatNumber = `${row.number}${seat.SeatNumber}`;
                  const isAvailable = seat.SeatAvailability === 'VAC';
                  const isTaken = isSeatTaken(seatNumber);
                  const seatPrice = parseFloat(seat.SeatCharacteristics || 0);
                  const isCurrentPassengerSeat = selectedSeats.some(s =>
                    s.passengerIndex === currentPassengerIndex && s.seatNumber === seatNumber
                  );

                  // Determine seat state
                  const isFreeAvailable = isAvailable && !isTaken && seatPrice === 0;
                  const isPaidAvailable = isAvailable && !isTaken && seatPrice > 0;
                  const isUnavailableOrTaken = !isAvailable || isTaken;
                  const seatFeatureLabel = buildSeatFeatureLabel(seat, row);

                  return (
                    <div className="hw-seatmap--seat-wrapper" key={seatIdx}>
                      <div
                        className={`hw-seatmap--seat ${
                          isFreeAvailable ? 'hw-seatmap--available-free' : 
                          isPaidAvailable ? 'hw-seatmap--available' : 
                          'hw-seatmap--not'
                        }${isCurrentPassengerSeat ? ' hw-seatmap--selected' : ''}`}
                        // title={
                        //   (isAvailable && !isTaken) ? `Seat ${seatNumber} - ${seat?.SeatCharacteristics}` : 'Sorry! This seat is taken'
                        // }
                        style={{ cursor: (isFreeAvailable || isPaidAvailable) ? 'pointer' : 'not-allowed' }}
                        onClick={() => {
                          if (isFreeAvailable || isPaidAvailable) {
                            const seatData = {
                              seatNumber,
                              price: seat.SeatPrice,
                              currency: seat.CurrencyCode,
                              row: row.number,
                              seat: seat.SeatNumber,
                              ...seat
                            };
                            handleSeatClick(seatData);
                          }
                        }}
                      >
                        <div className="hw-seatmap--seat-number">
                          {
                            (isFreeAvailable || isPaidAvailable) ? <div>
                              {!seat?.isLegroom ? (
                                  <span className="hw-seatmap--seat-text">
                                    {row.number}{seat.SeatNumber}
                                  </span>
                                ) : (
                                    <div className="hw-seatmap--legroom-seat">
                                      <span className="hw-seatmap--seat-text">
                                      <ChevronUpIcon  />
                                        {row.number}{seat.SeatNumber}
                                      <ChevronDownIcon  />
                                      </span>
                                    </div>
                                )}
                            </div>
                              : <svg width="20px" height="20px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4 6v2H2.5A1.5 1.5 0 001 9.5v9A1.5 1.5 0 002.5 20h15a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0017.5 8H16V6A6 6 0 004 6zm6-4a4 4 0 00-4 4v2h8V6a4 4 0 00-4-4zm1 14v-4H9v4h2z" fill="#5C5F62" /></svg>
                          }
                        </div>
                        <span className="hw-seatmap--tooltip">
                          {(isFreeAvailable || isPaidAvailable) ? (
                            <>
                              {seatNumber}{seatFeatureLabel ? ` · ${seatFeatureLabel}` : ''}{' - '}
                              {isFreeAvailable
                                ? 'Free'
                                : formatePriceFn(activeCurrency, formatPrice(seatPrice), displayPrice)}
                            </>
                          ) : (
                            "Sorry! This seat is taken"
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          ))}
        </div>
        <img src={FlightTail} style={{width:'100%'}} alt="Plane Tail" className="plane-tail" />
      </div>

      <div className='hw-passenger-data'>
        <div className=''>
          <div className="d-flex d-flex-column hw-gapy16 mt-3">
            <div className="d-flex align-items-center hw-gapy6">
              <div className="hw-fontBold hw-black">{seatSegment.departure.city}</div>
              <span className="hw-black"><ArrowRightIcon /></span>
              <div className="hw-fontBold hw-black">{seatSegment.arrival.city}</div>
            </div>
            <div className='hw-segment--data'>
              <small className='hw-segment-text'>{formatDayWiseDateFn(seatSegment.departure.time)}</small>
              <span className="dot-separator"></span>
              <small className='hw-segment-text'>{seatSegment?.stops === 0 ? 'Non Stop' : ''}</small>
              <span className="pipe-separator"></span>
              <small className='hw-segment-text'>{convertMinutesFn(seatSegment?.duration)}</small>
              <span className="dot-separator"></span>
              <span className="hw-text14 d-flex hw-gapy10 hw-black hw-fontWeight--500">
                <img alt={seatSegment?.airline?.name}
                  width="24" height="24"
                  src={airlineImageFn(seatSegment?.airline?.code)} />
              </span>
              <small className='ml-6 hw-segment-text'>{seatSegment?.airline?.code}-{seatSegment?.flightNumber}</small>
            </div>
            <div style={{width:'100%',height:'1px',background:"#D7D7D7"}} className='mt-3'></div>
          </div>

          {/* Passenger list */}
          <div className='mt-3'>
            <div className='d-flex d-flex-column'>
              {allPassengers.map((passenger, idx) => {
                if (passenger.passengerType === 'INFANT') return null;
                const seatNumber = getPassengerSeatNumber(idx);
                const isSelected = currentPassengerIndex === idx;
                const hasSeat = seatNumber !== null;

                return (
                  <div
                    key={idx}
                    className={`hw-passenger-detail ${isSelected ? 'hw-selected-passenger' : ''}`}
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => handlePassengerClick(idx)}
                  >
                      <div className='hw-fontBold'>
                        <FlightSeatIcon /><span className='ml-6'>
                          {passenger.originalTitle} :
                        </span>
                      </div>
                      <div className=' ml-6'>
                        {hasSeat ? seatNumber : 'Seat Not Selected'}
                      </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Confirm button - only show when all passengers have seats */}
        {allPassengersHaveSeats() && (
          <div className='hw-seatmap--confirm-price'>
            <div className='d-flex hw-gapy10'>
              <div className='hw-fontBold hw-black'>
                Total Price:
              </div>
              <div className='hw-primary hw-fontBold'>
              {currency} {formatPrice(totalPrice)}
              </div>
            </div>
            <div className='d-flex justify-content-end'>
              <button
                className="hw-btn hw-btn--primary hw-btn--lg pad1y pad2x"
                onClick={onConfirm}
              >
                Confirm Seating
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

SeatMap.propTypes = {
  seatMap: PropTypes.object.isRequired,
  seatSegment: PropTypes.object.isRequired,
  allPassengers: PropTypes.array,
  selectedSeats: PropTypes.array,
  onSeatSelect: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

export default SeatMap;
